import { useEffect, useState } from 'react';
import { ShieldAlert } from 'lucide-react';
import { apiRequest } from '../../lib/api';
import { API_PATHS } from '../../lib/apiRoutes';

interface ImpersonationStatus {
  active: boolean;
  impersonatorId: string | null;
  reason: string | null;
  currentUserId: string | null;
  role: string | null;
}

interface Props {
  isLoggedIn: boolean;
}

export default function ImpersonationBanner({ isLoggedIn }: Props) {
  const [status, setStatus] = useState<ImpersonationStatus | null>(null);

  useEffect(() => {
    if (!isLoggedIn) {
      setStatus(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await apiRequest<{ data?: ImpersonationStatus }>(API_PATHS.admin.impersonationStatus);
        if (!cancelled) {
          setStatus(response?.data ?? null);
        }
      } catch {
        if (!cancelled) {
          setStatus(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn]);

  if (!status?.active) {
    return null;
  }

  const handleStop = async () => {
    try {
      await apiRequest(API_PATHS.admin.impersonationStop, {
        method: 'POST',
        body: JSON.stringify({ targetUserId: status.currentUserId })
      });
      window.location.reload();
    } catch {
      // best-effort; reload anyway
      window.location.reload();
    }
  };

  return (
    <div
      role="alert"
      className="bg-amber-500 text-black py-2 px-4 text-center text-[10px] font-black uppercase tracking-[0.3em] flex items-center justify-center gap-3 z-[12001] sticky top-0"
    >
      <ShieldAlert className="w-3 h-3" />
      Impersonating user {status.currentUserId} ({status.role})
      {status.reason ? <span className="hidden md:inline">— Reason: {status.reason}</span> : null}
      <button
        onClick={handleStop}
        className="ml-2 bg-black/30 hover:bg-black/50 px-3 py-1 rounded transition-colors"
      >
        Stop impersonation
      </button>
    </div>
  );
}
